// Three-state theme toggle: system / light / dark.
// Persists choice to localStorage under key "zamba-theme".
// On mount, reads stored preference; "system" follows prefers-color-scheme.
// Applies "dark" class on <html>. The no-flash script in index.html runs
// synchronously before React mounts so there is no light-flash on first load.

import React, { useEffect, useState } from "react";
import { Sun, Moon, Monitor } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const STORAGE_KEY = "zamba-theme";

function applyTheme(preference) {
  const root = document.documentElement;
  if (preference === "dark") {
    root.classList.add("dark");
  } else if (preference === "light") {
    root.classList.remove("dark");
  } else {
    // system
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }
}

export default function ThemeToggle() {
  const [preference, setPreference] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "system";
    } catch {
      return "system";
    }
  });

  // Sync the <html> class whenever preference changes.
  useEffect(() => {
    applyTheme(preference);
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {}
  }, [preference]);

  // When preference is "system", follow the OS media query live.
  useEffect(() => {
    if (preference !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme("system");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [preference]);

  const icon =
    preference === "dark" ? (
      <Moon className="h-4 w-4" />
    ) : preference === "light" ? (
      <Sun className="h-4 w-4" />
    ) : (
      <Monitor className="h-4 w-4" />
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          title="Toggle theme"
          data-testid="theme-toggle"
        >
          {icon}
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-36">
        <DropdownMenuItem
          onClick={() => setPreference("light")}
          className="gap-2 cursor-pointer"
        >
          <Sun className="h-4 w-4" />
          Light
          {preference === "light" && (
            <span className="ml-auto text-[10px] text-muted-foreground">active</span>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setPreference("dark")}
          className="gap-2 cursor-pointer"
        >
          <Moon className="h-4 w-4" />
          Dark
          {preference === "dark" && (
            <span className="ml-auto text-[10px] text-muted-foreground">active</span>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setPreference("system")}
          className="gap-2 cursor-pointer"
        >
          <Monitor className="h-4 w-4" />
          System
          {preference === "system" && (
            <span className="ml-auto text-[10px] text-muted-foreground">active</span>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
